export class ApiError extends Error {
  constructor(public status: number, message: string, public issues?: unknown) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Only declare a JSON content type when JSON is actually being sent. Fastify
  // rejects an application/json request with an empty body, which would break
  // every bodyless POST (sign out, submit, withdraw, decline...).
  const sendsJson = init.body !== undefined && !(init.body instanceof FormData);

  const res = await fetch(`/api${path}`, {
    credentials: "same-origin",
    ...init,
    headers: {
      ...(sendsJson ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });

  if (res.status === 204) return undefined as T;

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(res.status, (body as any).error ?? res.statusText, (body as any).issues);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<T>(path, { method: "POST", body: form });
  },
};

export type Role = "STUDENT" | "STAFF" | "COORDINATOR" | "INDUSTRY";

export interface SessionUser {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  companyId: string | null;
}
